import type { ProductShippingDimensions } from '@/lib/functions/isProductShippable';
import type { SyliusAttribute, SyliusProduct, SyliusVariant } from '@/lib/types/products';

const DIMENSION_ATTRIBUTE_CODES: Record<keyof ProductShippingDimensions, string[]> = {
  width: ['WIDTH'],
  height: ['HEIGHT'],
  depth: ['DEPTH'],
  weight: ['WEIGHT'],
};

function parsePositiveNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }

  return numeric;
}

export type ShippingDimensionFields = {
  width?: number | null;
  height?: number | null;
  depth?: number | null;
  weight?: number | null;
};

export function buildShippingDimensionsFromFields(
  source?: ShippingDimensionFields | null,
): ProductShippingDimensions | null {
  if (!source) {
    return null;
  }

  const dimensions: ProductShippingDimensions = {
    width: parsePositiveNumber(source.width),
    height: parsePositiveNumber(source.height),
    depth: parsePositiveNumber(source.depth),
    weight: parsePositiveNumber(source.weight),
  };

  if (Object.values(dimensions).every((value) => value == null)) {
    return null;
  }

  return dimensions;
}

function dimensionsFromVariant(variant?: SyliusVariant | null): ProductShippingDimensions | null {
  return buildShippingDimensionsFromFields(variant);
}

function dimensionsFromAttributes(attributes?: SyliusAttribute[]): ProductShippingDimensions | null {
  if (!attributes?.length) {
    return null;
  }

  const dimensions: ProductShippingDimensions = {
    width: null,
    height: null,
    depth: null,
    weight: null,
  };

  for (const attribute of attributes) {
    const code = attribute.code?.toUpperCase();
    if (!code) {
      continue;
    }

    for (const [dimensionKey, codes] of Object.entries(DIMENSION_ATTRIBUTE_CODES) as Array<
      [keyof ProductShippingDimensions, string[]]
    >) {
      if (!codes.includes(code)) {
        continue;
      }

      dimensions[dimensionKey] = parsePositiveNumber(attribute.value);
    }
  }

  if (Object.values(dimensions).every((value) => value == null)) {
    return null;
  }

  return dimensions;
}

export function resolveProductShippingDimensions(
  syliusProduct: Pick<SyliusProduct, 'shippingDimensions' | 'attributes' | 'variantBlocks'>,
  preferredStoreNumber?: string | number,
): ProductShippingDimensions | null {
  if (syliusProduct.shippingDimensions) {
    return syliusProduct.shippingDimensions;
  }

  const storeKey = preferredStoreNumber != null ? String(preferredStoreNumber) : null;
  const preferredVariant =
    syliusProduct.variantBlocks?.find(
      (variant) => storeKey != null && variant.code?.startsWith(`${storeKey}-`),
    ) ?? syliusProduct.variantBlocks?.[0];

  return (
    dimensionsFromVariant(preferredVariant) ??
    dimensionsFromAttributes(syliusProduct.attributes) ??
    null
  );
}
